import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withErrorHandling } from "../api/axiosHelper";
import type { ApiError, ApiResponse } from "../api/types";

type Result<TData> = [
  TData | undefined,
  ApiError | undefined,
  {
    setData: Dispatch<SetStateAction<TData | undefined>>;
    isFetching: boolean;
    fetch: () => Promise<void>;
    /** Leaves the loading state and the error alone, unless it supersedes a non-silent fetch still in flight. */
    fetchSilent: () => Promise<ApiError | undefined>;
    cancel: () => void;
  },
];

interface Options<TData> {
  /**
   * When `true`, the fetch is skipped. Toggling from `false` to `true` after a successful fetch
   * does not reset `data` or `error` — previously fetched values persist until the next fetch.
   */
  disabled?: boolean;
  onSetData?: (data: TData) => void;
}

const useFetch = <TData>(
  apiCall: (signal?: AbortSignal) => Promise<ApiResponse<TData>>,
  options?: Options<TData>
): Result<TData> => {
  const { disabled, onSetData } = options ?? {};

  const [data, setData] = useState<TData>();
  const [error, setError] = useState<ApiError>();
  const [isFetching, setIsFetching] = useState(!disabled);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isSilentLoadRef = useRef(true);

  const handledApiCall = useMemo(() => withErrorHandling(apiCall), [apiCall]);

  const onSetDataRef = useRef(onSetData);

  useEffect(() => {
    onSetDataRef.current = onSetData;
  }, [onSetData]);

  const load = useCallback(
    async (silent: boolean): Promise<ApiError | undefined> => {
      if (disabled) {
        setIsFetching(false);
        return undefined;
      }

      const isSilent = silent && isSilentLoadRef.current;
      isSilentLoadRef.current = isSilent;

      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      if (!isSilent) {
        setIsFetching(true);
        setError(undefined);
      }

      const [responseData, responseError] = await handledApiCall(abortController.signal);

      if (abortController.signal.aborted || abortControllerRef.current !== abortController) {
        return undefined;
      }

      isSilentLoadRef.current = true;
      const fetchError = responseError?.isCanceledRequest ? undefined : responseError;

      if (responseError) {
        if (!isSilent) {
          setError(fetchError);
        }
      } else {
        setError(undefined);
        setData(responseData);
        onSetDataRef.current?.(responseData);
      }

      setIsFetching(false);
      return fetchError;
    },
    [disabled, handledApiCall]
  );

  const fetch = useCallback(async () => {
    await load(false);
  }, [load]);

  const fetchSilent = useCallback(() => load(true), [load]);

  useEffect(() => {
    load(false);

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [load]);

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    isSilentLoadRef.current = true;
  }, []);

  return [data, error, { setData, isFetching, fetch, fetchSilent, cancel }];
};

export default useFetch;
